import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search as SearchIcon, X, Loader2, ExternalLink, Youtube, FileText, Mic, Link2, MapPin, BookOpen, ArrowRight, Sparkles, ArrowLeft } from 'lucide-react';
import { useStore } from '../store/useStore';
import { client } from '../api/client';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useI18n } from '../i18n';
import { useTheme } from '../themes';

interface SearchResult {
  type: 'shift' | 'trail';
  id: number;
  title: string;
  subtitle: string;
  sourceType?: string;
  locationName?: string | null;
}

const Search: React.FC = () => {
  const { user, darkMode, entries, trails, feedShifts } = useStore();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { isSkeuomorphism, isNeubrutalism } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keyboard shortcut to open search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Disable background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Search logic
  const performSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      return;
    }

    setLoading(true);
    const q = searchQuery.toLowerCase();

    // Combine all available shifts
    const allShifts = user ? entries : feedShifts;
    const allTrails = trails || [];

    // Search shifts - only public items for logged out users, own + public for logged in
    const shiftResults: SearchResult[] = allShifts
      .filter(shift => {
        // Filter out private items (isPublic === 0) unless it's the user's own
        if (!user && shift.isPublic === 0) return false;
        if (user && shift.isPublic === 0 && shift.userId !== user.id) return false;
        
        return (
          shift.beforeBelief?.toLowerCase().includes(q) ||
          shift.afterBelief?.toLowerCase().includes(q) ||
          shift.sourceTitle?.toLowerCase().includes(q) ||
          shift.reflection?.toLowerCase().includes(q) ||
          shift.locationName?.toLowerCase().includes(q)
        );
      })
      .slice(0, 5)
      .map(shift => ({
        type: 'shift' as const,
        id: shift.id,
        title: shift.sourceTitle || shift.beforeBelief.substring(0, 50) + '...',
        subtitle: `${shift.sourceType || 'article'} · ${shift.afterBelief.substring(0, 40)}...`,
        sourceType: shift.sourceType || 'article',
        locationName: shift.locationName,
      }));

    // Search trails - only public trails
    const trailResults: SearchResult[] = allTrails
      .filter((trail: any) => {
        // Filter out private trails
        if (trail.isPublic === 0 && (!user || trail.userId !== user?.id)) return false;
        
        return (
          trail.title?.toLowerCase().includes(q) ||
          trail.description?.toLowerCase().includes(q)
        );
      })
      .slice(0, 3)
      .map((trail: any) => ({
        type: 'trail' as const,
        id: trail.id,
        title: trail.title,
        subtitle: trail.description || 'Learning trail',
      }));

    setResults([...shiftResults, ...trailResults]);
    setSelectedIndex(0);
    setLoading(false);
  }, [user, entries, feedShifts, trails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 200);

    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    }
  };

  const handleSelect = (result: SearchResult) => {
    setIsOpen(false);
    setQuery('');
    if (result.type === 'trail') {
      navigate('/trails');
    } else {
      // Navigate to the shift (could be in feed or my-shifts)
      if (user && entries.some(e => e.id === result.id)) {
        navigate('/my-shifts');
      } else {
        navigate('/');
      }
    }
  };

  const getSourceIcon = (sourceType?: string) => {
    if (sourceType === 'video') return <Youtube className="w-4 h-4 text-red-500" />;
    if (sourceType === 'paper') return <FileText className="w-4 h-4 text-orange-500" />;
    if (sourceType === 'podcast') return <Mic className="w-4 h-4 text-purple-500" />;
    return <Link2 className="w-4 h-4 text-brand-500" />;
  };

  // Handle touch on result items
  const handleResultTouch = (result: SearchResult, index: number) => {
    if (selectedIndex === index) {
      handleSelect(result);
    } else {
      setSelectedIndex(index);
    }
  };

  return (
    <>
      {/* Search Trigger Button - Mobile Optimized */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-2.5 rounded-xl sm:rounded-2xl transition-all duration-300 bg-neutral-100 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-800 group active:scale-95 touch-manipulation min-w-[44px] min-h-[44px] justify-center sm:justify-start"
        aria-label="Search"
      >
        <SearchIcon className="w-5 h-5 sm:w-4 sm:h-4 group-hover:scale-110 transition-transform" />
        <span className="hidden sm:inline text-sm font-bold">Search</span>
        <kbd className="hidden md:inline-flex items-center px-2 py-0.5 text-[10px] font-black rounded-lg bg-white dark:bg-neutral-800 text-neutral-400 shadow-sm border border-neutral-100 dark:border-neutral-700">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal - Full Screen on Mobile */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-start justify-center sm:pt-[12vh] px-0 sm:px-4">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-neutral-950/60 backdrop-blur-sm"
              onClick={() => setIsOpen(false)}
            />

            {/* Modal - Full screen on mobile, centered card on desktop */}
            <motion.div 
              ref={modalRef}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full h-full sm:h-auto sm:max-h-[80vh] sm:max-w-2xl overflow-hidden flex flex-col sm:border search-modal ${
                isNeubrutalism 
                  ? 'bg-white dark:bg-neutral-900 border-4 border-black dark:border-white shadow-[8px_8px_0_black] dark:shadow-[8px_8px_0_white]' 
                  : isSkeuomorphism 
                    ? 'bg-gradient-to-b from-amber-50 to-amber-100/90 dark:from-neutral-900 dark:to-neutral-800 border-double border-3 border-amber-300 dark:border-amber-700 sm:rounded-2xl shadow-2xl' 
                    : 'bg-white dark:bg-neutral-900 sm:rounded-[32px] shadow-2xl sm:border-neutral-200 dark:sm:border-neutral-700'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search Input - Larger touch targets on mobile */}
              <div className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-2.5 -ml-1 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center sm:hidden"
                  aria-label="Close search"
                >
                  <ArrowLeft className="w-5 h-5 text-neutral-500" />
                </button>
                <SearchIcon className="w-5 h-5 sm:w-6 sm:h-6 text-brand-600 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t.common.searchPlaceholder}
                  className="flex-1 bg-transparent outline-none text-lg sm:text-xl font-bold placeholder-neutral-400 dark:placeholder-neutral-600 min-h-[44px]"
                  autoFocus
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
                {query ? (
                  <button 
                    onClick={() => setQuery('')} 
                    className="p-2.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Clear search"
                  >
                    <X className="w-5 h-5 text-neutral-400" />
                  </button>
                ) : (
                  <button 
                    onClick={() => setIsOpen(false)}
                    className="hidden sm:flex p-2.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors min-w-[44px] min-h-[44px] items-center justify-center"
                    aria-label="Close search"
                  >
                    <X className="w-5 h-5 text-neutral-400" />
                  </button>
                )}
              </div>

              {/* Results - Optimized for touch scrolling */}
              <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-hide -webkit-overflow-scrolling-touch">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 sm:py-20">
                    <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-brand-600 mb-4" />
                    <p className="text-neutral-400 font-bold text-xs sm:text-sm uppercase tracking-widest">Searching...</p>
                  </div>
                ) : results.length > 0 ? (
                  <div className="p-2 sm:p-3">
                    {results.map((result, index) => (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleSelect(result)}
                        onTouchStart={() => setSelectedIndex(index)}
                        className={`w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 sm:py-4 rounded-2xl sm:rounded-3xl transition-all duration-300 touch-manipulation ${
                          index === selectedIndex
                            ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/20 scale-[1.01] sm:scale-[1.02]'
                            : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 active:bg-neutral-200 dark:active:bg-neutral-700'
                        }`}
                      >
                        <div className={`w-12 h-12 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center transition-colors flex-shrink-0 ${
                          index === selectedIndex
                            ? 'bg-white/20'
                            : result.type === 'trail'
                              ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600'
                              : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500'
                        }`}>
                          {result.type === 'trail' 
                            ? <BookOpen className="w-5 h-5 sm:w-6 sm:h-6" />
                            : getSourceIcon(result.sourceType)
                          }
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className={`font-bold truncate text-base sm:text-lg ${index === selectedIndex ? 'text-white' : 'text-neutral-900 dark:text-neutral-100'}`}>
                            {result.title}
                          </p>
                          <div className={`flex items-center gap-2 text-sm sm:text-sm font-medium ${index === selectedIndex ? 'text-brand-100' : 'text-neutral-400'}`}>
                            <span className="truncate">{result.subtitle}</span>
                            {result.locationName && (
                              <>
                                <span>·</span>
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                  <span className="max-w-[80px] sm:max-w-none truncate">{result.locationName}</span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <ArrowRight className={`w-5 h-5 sm:w-5 sm:h-5 flex-shrink-0 transition-transform ${index === selectedIndex ? 'translate-x-1 text-white' : 'text-neutral-300'}`} />
                      </button>
                    ))}
                  </div>
                ) : query ? (
                  <div className="py-16 sm:py-24 text-center px-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-neutral-100 dark:bg-neutral-900 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <SearchIcon className="w-8 h-8 sm:w-10 sm:h-10 text-neutral-300 dark:text-neutral-800" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2">{t.common.noResults}</h3>
                    <p className="text-neutral-400 font-medium text-sm sm:text-base">{t.common.searchPlaceholder}</p>
                  </div>
                ) : (
                  <div className="py-16 sm:py-20 text-center px-4">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-brand-50 dark:bg-brand-900/20 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-brand-500" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold mb-2 text-neutral-900 dark:text-neutral-100">{t.nav.search}</h3>
                    <p className="text-neutral-500 dark:text-neutral-400 font-medium text-sm sm:text-base">{t.common.searchPlaceholder}</p>
                  </div>
                )}
              </div>

              {/* Footer - Hidden on mobile for more screen space */}
              <div className="hidden sm:flex items-center justify-between px-8 py-4 border-t border-neutral-200 dark:border-neutral-800 bg-neutral-100 dark:bg-neutral-900/50">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-400">
                    <kbd className="px-1.5 py-0.5 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 shadow-sm">↑↓</kbd>
                    <span>Navigate</span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-400">
                    <kbd className="px-1.5 py-0.5 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 shadow-sm">↵</kbd>
                    <span>Select</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-neutral-400">
                  <kbd className="px-1.5 py-0.5 rounded-lg bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 shadow-sm">ESC</kbd>
                  <span>Close</span>
                </div>
              </div>

              {/* Mobile Footer - Touch hint */}
              <div className="sm:hidden flex items-center justify-center px-4 py-3 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                <p className="text-xs font-medium text-neutral-400">Tap a result to select</p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Search;
